/**
 * Date Formatting Functions
 *
 * All date formatting should use these functions for consistency.
 * All functions handle null/undefined inputs gracefully with fallbacks.
 */

import { format, formatISO, parse, parseISO, isValid } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import type { DateInput, FormatOptions, DateRangeOptions } from './types';
import {
  DEFAULT_LOCALE,
  DEFAULT_FALLBACK,
  DATE_FORMATS,
  getUserTimezone,
} from './config';

// =============================================================================
// Core Parsing
// =============================================================================

/**
 * Parse any date input into a Date object
 * Returns null if invalid
 */
export function parseDate(input: DateInput): Date | null {
  if (!input) return null;

  let date: Date;

  if (input instanceof Date) {
    date = input;
  } else if (typeof input === 'string') {
    const trimmed = input.trim();

    // Keep ISO/API strings fast and deterministic.
    const isIsoLike = /^\d{4}-\d{2}-\d{2}(?:[T\s].*)?$/.test(trimmed);
    if (isIsoLike) {
      date = parseISO(trimmed);
    } else {
      // Enforce day-first parsing for user-facing dates.
      const digitsOnly = trimmed.replace(/\D/g, '');
      const knownFormats = [DATE_FORMATS.short, 'dd/MM/yyyy', 'ddMMyyyy'] as const;
      let parsedKnown: Date | null = null;
      for (const fmt of knownFormats) {
        if (fmt === 'ddMMyyyy' && digitsOnly.length !== 8) continue;
        const toTry = fmt === 'ddMMyyyy' ? digitsOnly : trimmed;
        const d = parse(toTry, fmt, new Date());
        if (isValid(d)) {
          parsedKnown = d;
          break;
        }
      }
      date = parsedKnown ?? new Date(trimmed);
    }
  } else if (typeof input === 'number') {
    date = new Date(input);
  } else {
    return null;
  }

  return isValid(date) ? date : null;
}

/**
 * Check if a date input is valid
 */
export function isValidDate(input: DateInput): boolean {
  return parseDate(input) !== null;
}

/**
 * Parse a user-typed date string (e.g. from a date range input).
 * Tries parseDate (ISO, etc.) then dd-MM-yyyy, dd/MM/yyyy, and 8-digit ddmmyyyy.
 */
export function parseDateFromInput(str: string): Date | null {
  const trimmed = str.trim();
  if (!trimmed) return null;

  const formats = [DATE_FORMATS.short, 'dd/MM/yyyy', 'ddMMyyyy'] as const;
  const digitsOnly = trimmed.replace(/\D/g, '');

  // IMPORTANT:
  // Parse explicit user-input formats first (dd-MM-yyyy / dd/MM/yyyy) to avoid
  // locale-dependent Date constructor behavior like MM-dd-yyyy reinterpretation.
  for (const fmt of formats) {
    if (fmt === 'ddMMyyyy' && digitsOnly.length !== 8) continue;
    const toTry = fmt === 'ddMMyyyy' ? digitsOnly : trimmed;
    try {
      const d = parse(toTry, fmt, new Date());
      if (isValid(d)) return d;
    } catch {
      // continue
    }
  }

  // Fallback only for ISO-like strings and API datetime values.
  const isIsoLike = /^\d{4}-\d{2}-\d{2}(?:[T\s].*)?$/.test(trimmed);
  if (isIsoLike) {
    const parsed = parseDate(trimmed);
    if (parsed) return parsed;
  }

  return null;
}

/**
 * Format-as-you-type for date inputs: normalize to display format (dd-MM-yyyy).
 * Converts pasted ISO to short format; inserts dashes when typing digits only.
 */
export function formatDateInputAsYouType(
  value: string,
  displayFormat: string = DATE_FORMATS.short
): string {
  const trimmed = value.trim();
  if (!trimmed) return value;
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    const parsed = parseDate(trimmed);
    if (parsed) return format(parsed, displayFormat);
  }
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4, 8)}`;
}

/**
 * Format a date string for API requests (full ISO string).
 * Returns empty string if input is invalid.
 * Use for: query params (e.g. gte-From, lte-To) that expect ISO datetime.
 */
export function formatDateForApi(dateStr: string): string {
  if (!dateStr || !dateStr.trim()) return '';
  const date = parseDate(dateStr.trim());
  return date ? formatISO(date) : '';
}

// =============================================================================
// Standard Formats
// =============================================================================

/**
 * Short date format: "07/01/2026"
 * Use for: table cells, compact displays
 */
export function formatDateShort(
  input: DateInput,
  options: FormatOptions = {}
): string {
  const {
    locale = DEFAULT_LOCALE,
    fallback = DEFAULT_FALLBACK,
    timezone,
    showTimezone = false,
  } = options;

  const date = parseDate(input);
  if (!date) return fallback;

  const tz = timezone || getUserTimezone();
  let result = formatInTimeZone(date, tz, DATE_FORMATS.short, { locale });

  if (showTimezone) {
    const tzAbbr = formatInTimeZone(date, tz, 'zzz', { locale });
    result += ` ${tzAbbr}`;
  }

  return result;
}

/**
 * Medium date format: "07 Jan 2026"
 * Use for: headers, summaries
 */
export function formatDateMedium(
  input: DateInput,
  options: FormatOptions = {}
): string {
  const {
    locale = DEFAULT_LOCALE,
    fallback = DEFAULT_FALLBACK,
    timezone,
    showTimezone = false,
  } = options;

  const date = parseDate(input);
  if (!date) return fallback;

  const tz = timezone || getUserTimezone();
  let result = formatInTimeZone(date, tz, DATE_FORMATS.medium, { locale });

  if (showTimezone) {
    const tzAbbr = formatInTimeZone(date, tz, 'zzz', { locale });
    result += ` ${tzAbbr}`;
  }

  return result;
}

/**
 * Long date format: "7 January 2026"
 * Use for: detail views, formal displays
 */
export function formatDateLong(
  input: DateInput,
  options: FormatOptions = {}
): string {
  const {
    locale = DEFAULT_LOCALE,
    fallback = DEFAULT_FALLBACK,
    timezone,
    showTimezone = false,
  } = options;

  const date = parseDate(input);
  if (!date) return fallback;

  const tz = timezone || getUserTimezone();
  let result = formatInTimeZone(date, tz, DATE_FORMATS.long, { locale });

  if (showTimezone) {
    const tzAbbr = formatInTimeZone(date, tz, 'zzz', { locale });
    result += ` ${tzAbbr}`;
  }

  return result;
}

/**
 * Date with time: "07/01/2026, 14:30"
 * Use for: timestamps, activity logs
 */
export interface DateTimeRangeInput {
  /** ISO date or date-time (e.g. `2025-12-01` or `2025-12-01T00:00:00Z`). */
  date?: string | null;
  /** Local time in `HH:mm` or `HH:mm:ss`. */
  timeStart?: string | null;
  /** Local time in `HH:mm` or `HH:mm:ss`. */
  timeEnd?: string | null;
}

/** Formats YYYY-MM-DD (or ISO datetime prefix) as calendar date without timezone shift. */
function formatCalendarDatePart(dateStr: string): string | null {
  const dateOnly = dateStr.length >= 10 ? dateStr.slice(0, 10) : dateStr.trim();
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateOnly);
  if (match) {
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const localDate = new Date(year, month - 1, day);
    if (!isValid(localDate)) return null;
    return format(localDate, DATE_FORMATS.dashed, { locale: DEFAULT_LOCALE });
  }

  const parsed = parseDate(dateOnly);
  if (!parsed) return null;
  return format(parsed, DATE_FORMATS.dashed, { locale: DEFAULT_LOCALE });
}

function formatTimeToAmPm(time: string | null | undefined): string | null {
  if (!time?.trim()) return null;
  const [hoursRaw = '0', minutesRaw = '0'] = time.trim().split(':');
  const hours = Number.parseInt(hoursRaw, 10);
  const minutes = Number.parseInt(minutesRaw, 10);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;

  // Wall-clock times from the API — format as 12h with AM/PM (e.g. 09:00 AM).
  const anchor = new Date(2000, 0, 1, hours, minutes, 0, 0);
  return format(anchor, 'hh:mm a', { locale: DEFAULT_LOCALE });
}

/**
 * Date with optional time range: `DD-MM-YYYY, hh:mm a-hh:mm a` or `DD-MM-YYYY, hh:mm a`.
 * Omits the end time when missing or equal to start.
 */
export function formatDateTimeRange(
  input: DateTimeRangeInput,
  options: Pick<FormatOptions, 'fallback'> = {}
): string {
  const { date, timeStart, timeEnd } = input;
  const fallback = options.fallback ?? DEFAULT_FALLBACK;

  if (!date?.trim()) return fallback;

  const datePart = formatCalendarDatePart(date);
  if (!datePart) return fallback;
  const start = formatTimeToAmPm(timeStart);
  const end = formatTimeToAmPm(timeEnd);

  if (!start) return datePart;
  if (end && end !== start) return `${datePart}, ${start}-${end}`;
  return `${datePart}, ${start}`;
}

/** Like {@link formatDateTimeRange} but returns `null` when no date is set. */
export function formatDateTimeRangeOrNull(input: DateTimeRangeInput): string | null {
  if (!input.date) return null;
  return formatDateTimeRange(input);
}

export function formatDateTime(
  input: DateInput,
  options: FormatOptions = {}
): string {
  const {
    locale = DEFAULT_LOCALE,
    fallback = DEFAULT_FALLBACK,
    timezone,
    showTimezone = false,
  } = options;

  const date = parseDate(input);
  if (!date) return fallback;

  const tz = timezone || getUserTimezone();
  let result = formatInTimeZone(date, tz, DATE_FORMATS.dateTime, { locale });

  if (showTimezone) {
    const tzAbbr = formatInTimeZone(date, tz, 'zzz', { locale });
    result += ` ${tzAbbr}`;
  }

  return result;
}

/**
 * Date with time and timezone: "07/01/2026, 14:30 CET"
 * Use for: international contexts, shipment tracking
 */
export function formatDateTimeTz(
  input: DateInput,
  options: Omit<FormatOptions, 'showTimezone'> = {}
): string {
  return formatDateTime(input, { ...options, showTimezone: true });
}

/**
 * Time only: "14:30"
 * Use for: time displays, schedules
 */
export function formatTime(
  input: DateInput,
  options: FormatOptions = {}
): string {
  const {
    locale = DEFAULT_LOCALE,
    fallback = DEFAULT_FALLBACK,
    timezone,
    showTimezone = false,
  } = options;

  const date = parseDate(input);
  if (!date) return fallback;

  const tz = timezone || getUserTimezone();
  let result = formatInTimeZone(date, tz, DATE_FORMATS.time, { locale });

  if (showTimezone) {
    const tzAbbr = formatInTimeZone(date, tz, 'zzz', { locale });
    result += ` ${tzAbbr}`;
  }

  return result;
}

/**
 * ISO date format: "2026-01-07"
 * Use for: API calls, data attributes, sorting
 */
export function formatDateISO(
  input: DateInput,
  options: Pick<FormatOptions, 'fallback'> = {}
): string {
  const { fallback = DEFAULT_FALLBACK } = options;
  const date = parseDate(input);

  if (!date) return fallback;

  return format(date, DATE_FORMATS.iso);
}

/**
 * Extract date-only string from ISO datetime: "2026-01-07T14:30:00Z" -> "2026-01-07"
 * Use for: Native HTML date inputs which require YYYY-MM-DD format
 *
 * This function extracts the date portion directly from the string
 * without timezone conversion, preserving the date as stored in the API.
 *
 * @example
 * toDateInputValue("2026-01-15T11:00:00Z") => "2026-01-15"
 * toDateInputValue(null) => undefined
 * toDateInputValue("") => undefined
 */
export function toDateInputValue(
  isoString: string | null | undefined
): string | undefined {
  if (!isoString) return undefined;
  // Extract the date part (first 10 characters: YYYY-MM-DD)
  // This preserves the date as-is without timezone conversion
  return isoString.slice(0, 10);
}

// =============================================================================
// Specialized Formats
// =============================================================================

/**
 * Month and year only: "January 2026"
 * Use for: month pickers, reports
 */
export function formatMonthYear(
  input: DateInput,
  options: FormatOptions = {}
): string {
  const {
    locale = DEFAULT_LOCALE,
    fallback = DEFAULT_FALLBACK,
    timezone,
  } = options;

  const date = parseDate(input);
  if (!date) return fallback;

  const tz = timezone || getUserTimezone();
  return formatInTimeZone(date, tz, DATE_FORMATS.monthYear, { locale });
}

/**
 * Day of week with date: "Monday, 7 January 2026"
 * Use for: scheduling, calendars
 */
export function formatDateWithWeekday(
  input: DateInput,
  options: FormatOptions = {}
): string {
  const {
    locale = DEFAULT_LOCALE,
    fallback = DEFAULT_FALLBACK,
    timezone,
  } = options;

  const date = parseDate(input);
  if (!date) return fallback;

  const tz = timezone || getUserTimezone();
  return formatInTimeZone(date, tz, DATE_FORMATS.withWeekday, { locale });
}

/**
 * Full date and time: "Monday, 7 January 2026 at 14:30"
 * Use for: detailed timestamps, confirmations
 */
export function formatDateFull(
  input: DateInput,
  options: FormatOptions = {}
): string {
  const {
    locale = DEFAULT_LOCALE,
    fallback = DEFAULT_FALLBACK,
    timezone,
    showTimezone = false,
  } = options;

  const date = parseDate(input);
  if (!date) return fallback;

  const tz = timezone || getUserTimezone();
  let result = formatInTimeZone(date, tz, DATE_FORMATS.full, { locale });

  if (showTimezone) {
    const tzAbbr = formatInTimeZone(date, tz, 'zzz', { locale });
    result += ` ${tzAbbr}`;
  }

  return result;
}

/**
 * Date range: "07/01/2026 - 14/01/2026"
 * Use for: date ranges, periods
 */
export function formatDateRange(
  start: DateInput,
  end: DateInput,
  options: DateRangeOptions = {}
): string {
  const { separator = ' - ', fallback = DEFAULT_FALLBACK, ...formatOpts } = options;

  const startFormatted = formatDateShort(start, { ...formatOpts, fallback });
  const endFormatted = formatDateShort(end, { ...formatOpts, fallback });

  if (startFormatted === fallback && endFormatted === fallback) {
    return fallback;
  }

  return `${startFormatted}${separator}${endFormatted}`;
}

/**
 * Chat message timestamp: "Nov 27 at 9:15 AM"
 * Use for: chat messages, real-time communication displays
 */
export function formatChatTimestamp(
  input: DateInput,
  options: FormatOptions = {}
): string {
  const {
    locale = DEFAULT_LOCALE,
    fallback = DEFAULT_FALLBACK,
    timezone,
  } = options;

  const date = parseDate(input);
  if (!date) return fallback;

  const tz = timezone || getUserTimezone();
  // Format: "Nov 27 at 9:15 AM"
  return formatInTimeZone(date, tz, "MMM d 'at' h:mm a", { locale });
}

/**
 * Dashed date format: "04-04-2024" (DD-MM-YYYY)
 * Use for: tables, BBD/expiry, display-only fields that use dashes
 */
export function formatDateDashed(
  input: DateInput,
  options: FormatOptions = {}
): string {
  return formatCustom(input, DATE_FORMATS.dashed, options);
}

/**
 * Custom format using date-fns format string
 * Use for: special cases not covered by standard formats
 *
 * @see https://date-fns.org/docs/format for format string reference
 */
export function formatCustom(
  input: DateInput,
  formatString: string,
  options: FormatOptions = {}
): string {
  const {
    locale = DEFAULT_LOCALE,
    fallback = DEFAULT_FALLBACK,
    timezone,
  } = options;

  const date = parseDate(input);
  if (!date) return fallback;

  const tz = timezone || getUserTimezone();
  return formatInTimeZone(date, tz, formatString, { locale });
}

// =============================================================================
// Form Input Helpers
// =============================================================================

/**
 * Split an ISO datetime string into local date and time parts for form inputs.
 * Converts from UTC to the user's timezone so form fields display local time.
 * Time is `HH:mm` (no seconds in the UI). Seconds default to `:00` when combining for API.
 *
 * @example
 * // User in CET (UTC+1)
 * splitDateTimeLocal("2026-02-16T10:00:00Z") => { date: "2026-02-16", time: "11:00" }
 * splitDateTimeLocal(null)                    => { date: "", time: "" }
 */
export function splitDateTimeLocal(
  isoString: string | null | undefined
): { date: string; time: string } {
  if (!isoString) return { date: '', time: '' };
  const parsed = parseISO(isoString);
  if (isNaN(parsed.getTime())) return { date: '', time: '' };
  const tz = getUserTimezone();
  return {
    date: formatInTimeZone(parsed, tz, 'yyyy-MM-dd'),
    time: formatInTimeZone(parsed, tz, 'HH:mm'),
  };
}

/**
 * Combine local date and time form inputs into a UTC ISO string.
 * The date and time are treated as local (user's timezone) and converted to UTC.
 *
 * @param date - Date string in YYYY-MM-DD format (local)
 * @param time - Time string in HH:mm or HH:mm:ss format (local), defaults to "09:00:00" if empty
 * @returns UTC ISO string (e.g. "2026-02-16T10:00:00.000Z"), or null if date is empty
 *
 * @example
 * // User in CET (UTC+1)
 * combineDateTimeToUTC("2026-02-16", "11:00")    => "2026-02-16T10:00:00.000Z"
 * combineDateTimeToUTC("2026-02-16", "11:00:00") => "2026-02-16T10:00:00.000Z"
 * combineDateTimeToUTC("2026-02-16", "")          => "2026-02-16T08:00:00.000Z" (09:00 CET)
 * combineDateTimeToUTC("", "11:00")               => null
 */
export function combineDateTimeToUTC(
  date: string,
  time: string
): string | null {
  if (!date) return null;
  const normalizedTime = time
    ? (time.split(':').length === 3 ? time : `${time}:00`)
    : '09:00:00';
  const parsed = parseISO(`${date}T${normalizedTime}`);
  if (isNaN(parsed.getTime())) return null;
  return parsed.toISOString();
}
